/**
 * ReportWizard — Guided simple PDF report for non-technical users.
 * Generates a real map PDF via PrintService when the map is ready.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  LinearProgress,
} from '@mui/material';
import PictureAsPdf from '@mui/icons-material/PictureAsPdf';
import CheckCircle from '@mui/icons-material/CheckCircle';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { endTaskWizard, addNotification } from '../../store/slices/uiSlice';
import {
  openPrintDialog,
  setPrintTitle,
  setPrintOptions,
} from '../../store/slices/printExportSlice';
import { mapNavigator } from '../../services/MapNavigator';
import { formatArea, formatCoordinates, formatDistance } from '../../services/MeasurementService';
import { generatePDF, downloadBlob } from '../../services/PrintService';

interface ReportWizardProps {
  open: boolean;
  onClose: () => void;
}

const STEPS = ['Choose what to include', 'Add your notes', 'Generate & download'];

const ReportWizard: React.FC<ReportWizardProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const layers = useAppSelector((s) => s.layers.layers);
  const printOptions = useAppSelector((s) => s.printExport.printOptions);
  const measurement = useAppSelector((s) => s.measurement);

  const [activeStep, setActiveStep] = useState(0);
  const [includeMap, setIncludeMap] = useState(true);
  const [includeMeasurements, setIncludeMeasurements] = useState(true);
  const [notes, setNotes] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setIncludeMap(true);
      setIncludeMeasurements(true);
      setNotes('');
      setIsGenerating(false);
    }
  }, [open]);

  const measurementLines = includeMeasurements
    ? [
        measurement.currentDistance > 0
          ? `Distance: ${formatDistance(measurement.currentDistance, measurement.distanceUnits)}`
          : null,
        measurement.currentArea > 0
          ? `Area: ${formatArea(measurement.currentArea, measurement.areaUnits)}`
          : null,
        measurement.currentPerimeter > 0
          ? `Perimeter: ${formatDistance(measurement.currentPerimeter, measurement.distanceUnits)}`
          : null,
        measurement.clickedCoordinate
          ? `Coordinate: ${formatCoordinates(
              measurement.clickedCoordinate,
              measurement.coordinateFormat
            )}`
          : null,
      ].filter((line): line is string => line !== null)
    : [];

  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const title = 'GeoVera Simple Report';
      const options = {
        ...printOptions,
        title,
        includeTitle: true,
        includeLegend: includeMap,
        includeScaleBar: includeMap,
        includeNorthArrow: includeMap,
        orientation: includeMap ? ('landscape' as const) : printOptions.orientation,
        pageSize: (printOptions.pageSize || 'A4') as typeof printOptions.pageSize,
      };

      dispatch(setPrintTitle(title));
      dispatch(setPrintOptions(options));

      const map = includeMap ? mapNavigator.getMap() : null;
      if (includeMap && !map) {
        dispatch(openPrintDialog());
        dispatch(
          addNotification({
            type: 'warning',
            message: 'Map still loading — use the print dialog when it is ready.',
          })
        );
        return;
      }

      if (map) {
        try {
          map.updateSize();
          map.renderSync();
        } catch {
          /* map mock / not ready */
        }
        await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));
      }

      const legendLayers = includeMap
        ? layers
            .filter((layer) => layer.visible)
            .map((layer) => ({
              name: layer.name,
              color: '#4fc3f7',
              geometryType: layer.geometryType || 'Polygon',
            }))
        : undefined;

      const blob = generatePDF(map, options, legendLayers, {
        notes: notes.trim(),
        measurements: measurementLines,
      });
      downloadBlob(blob, 'geovera-simple-report.pdf');

      dispatch(
        addNotification({
          type: 'success',
          message: 'Your report PDF was downloaded successfully.',
        })
      );
      dispatch(endTaskWizard());
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not generate report';
      dispatch(addNotification({ type: 'error', message: msg }));
      if (includeMap) dispatch(openPrintDialog());
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    dispatch(endTaskWizard());
    onClose();
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              What should go in your report?
            </Typography>
            <Typography paragraph>We&apos;ll create a clean PDF you can email or print.</Typography>

            <FormControlLabel
              control={
                <Checkbox checked={includeMap} onChange={(e) => setIncludeMap(e.target.checked)} />
              }
              label="Map view of your area"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeMeasurements}
                  onChange={(e) => setIncludeMeasurements(e.target.checked)}
                />
              }
              label="Include current measurements"
            />

            <Alert severity="info" sx={{ mt: 2 }}>
              {measurementLines.length > 0
                ? `${measurementLines.length} current measurement(s) will be included.`
                : 'No current measurements are available. Run Measure first to add them to the report.'}
            </Alert>
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Add any notes or description
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={5}
              label="Notes for the report (optional)"
              placeholder="e.g. This boundary is for the new shed approval."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Typography variant="body2" color="text.secondary">
              These notes are included as a dedicated section in the PDF.
            </Typography>
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Ready to create your report
            </Typography>
            <Typography paragraph>
              We&apos;ll generate a PDF with everything you selected.
            </Typography>

            <Box sx={{ mb: 2 }}>
              {includeMap && <Typography variant="body2">✓ Map included</Typography>}
              {includeMeasurements && measurementLines.length > 0 && (
                <Typography variant="body2">
                  ✓ {measurementLines.length} current measurement(s) included
                </Typography>
              )}
              {includeMeasurements && measurementLines.length === 0 && (
                <Typography variant="body2">No current measurements available</Typography>
              )}
              {notes.trim() && <Typography variant="body2">✓ Your notes added</Typography>}
            </Box>

            <Alert severity="success">
              The report downloads as a PDF when generation finishes.
            </Alert>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Create a Simple Report — Step {activeStep + 1} of {STEPS.length}
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent(activeStep)}

        {isGenerating && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              Generating your report...
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        {activeStep > 0 && <Button onClick={handleBack}>Back</Button>}
        {activeStep < STEPS.length - 1 ? (
          <Button variant="contained" onClick={handleNext} endIcon={<CheckCircle />}>
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleGenerate}
            color="success"
            disabled={isGenerating}
            startIcon={<PictureAsPdf />}
          >
            Generate PDF Report
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReportWizard;
